import { useContext, useMemo } from "react";
import classNames from "classnames";
import { PaginatedTransactionsTableContext } from "../PaginatedTransactionsTableContextProvider";
import { ViewTable } from "../../views/ViewTable";
import { useBulkActions } from "../TransactionsBulkActions/useBulkActions";
import { NoTransactions } from "../NoTransactions";
import { TransactionView } from "../../../common/types/views/transactionViews";
import styles from "./TransactionsView.module.scss";
import { TransactionsViewFooter } from "./TransactionsViewFooter";
import { transactionsViewConfig } from "./transactionsViewConfig";

interface Props {
    view: TransactionView;
    setSelectedTransactionId?: (id: number | string | undefined) => void;
    selectedTransactionId?: number;
    useCache?: boolean;
    scrollContainerRef?: React.RefObject<HTMLElement>;
    className?: string;
    stickyFooter?: boolean;
    emptyView?: () => React.ReactNode;
}

export const TransactionsView: React.FC<Props> = ({
    view,
    setSelectedTransactionId,
    selectedTransactionId,
    scrollContainerRef,
    className,
    stickyFooter = true,
    emptyView = () => <NoTransactions />,
}) => {
    const { transactions, handleSortChange, currentSort } = useContext(
        PaginatedTransactionsTableContext,
    );
    const data = useMemo(() => transactions ?? null, [transactions]);
    const bulkActions = useBulkActions();

    const rowSelection = useMemo(() => {
        if (!bulkActions.enabled) {
            return {};
        }
        if (bulkActions.hasSelectedAll) {
            // Select all visible rows
            return Object.fromEntries(data?.map((row) => [row.id, true]) ?? []);
        }
        const selectedIds = Object.keys(bulkActions.selected);
        const selection: Record<string, boolean> = {};
        for (const id of selectedIds) {
            selection[id] = true;
        }
        return selection;
    }, [
        bulkActions.selected,
        bulkActions.enabled,
        bulkActions.hasSelectedAll,
        data,
    ]);

    const handleRowSelectionChange = (
        updater:
            | Record<string, boolean>
            | ((prev: Record<string, boolean>) => Record<string, boolean>),
    ) => {
        if (!bulkActions.enabled) {
            return;
        }
        if (!data) {
            return;
        }
        let nextSelection: Record<string, boolean>;
        if (typeof updater === "function") {
            nextSelection = updater(rowSelection);
        } else {
            nextSelection = updater;
        }
        const selectedIds = Object.keys(nextSelection).filter(
            (id) => nextSelection[id],
        );
        if (selectedIds.length === 0) {
            bulkActions.clearSelection();
        } else if (selectedIds.length === data.length) {
            bulkActions.selectAll();
        } else {
            const selectedRows = data.filter((row) =>
                selectedIds.includes(String(row.id)),
            );
            bulkActions.clearSelection();
            bulkActions.select(selectedRows);
        }
    };

    return (
        <div className={classNames(styles.transactionsView, className)}>
            <div className={styles.viewWrapper}>
                <ViewTable
                    data={data}
                    view={view}
                    config={transactionsViewConfig}
                    selectedRowId={selectedTransactionId}
                    onRowClick={(row) => setSelectedTransactionId?.(row.id)}
                    rowSelection={rowSelection}
                    onRowSelectionChange={handleRowSelectionChange}
                    sort={currentSort}
                    onSortChange={handleSortChange}
                    scrollContainerRef={scrollContainerRef}
                    emptyView={emptyView}
                />
                <TransactionsViewFooter sticky={stickyFooter} />
            </div>
        </div>
    );
};
